<template>
	<div>
		<ts-loading-banner :loading="loading">
			<!-- Header -->
			<div class="ts-flex ts-justify-between ts-items-start ts-mb-3">
				<div class="ts-bg-blue-50 ts-px-3 ts-py-2 ts-rounded ts-border ts-border-blue-200">
					<h1 class="ts-text-sm ts-font-semibold ts-text-blue-800">{{ (student.first_name_kh || '') + ' ' +
						(student.last_name_kh || '')
						}}</h1>
					<h2 class="ts-text-xs ts-text-blue-600">{{ (student.first_name_en || '') + ' ' + (student.last_name_en || '')
						}}</h2>
					<h2 class="ts-text-xs ts-text-blue-600"><i>{{ student.gender }}</i></h2>
				</div>
				<div class="ts-text-right ts-text-xs">
					<div class="ts-text-blue-600 ts-font-medium">{{ getSessionName }}</div>
					<div class="ts-text-gray-500">{{ componentName && componentName.name }}</div>
				</div>
			</div>

			<!-- Homework Scores -->
			<div class="ts-space-y-2 ts-rounded-md ts-border ts-border-gray-200 ts-p-2">
				<div class="ts-flex ts-items-center ts-space-x-2" v-for="(score, index) in componentModels.component_items"
					:key="index">
					<div class="ts-w-32">
						<p class="ts-text-sm">{{ score.name }}</p>
					</div>
					<div>
						<input v-model="score.actual_score" :ref="`student_${student.student_id}_${score.subject_comp_detail_id}`"
							type="number" min="-1" @wheel="handleWheel" @keyup.enter="onStore" :max="score.max_score"
							@input="validateInput(score, score.max_score)"
							class="ts-border ts-py-1 ts-px-2 ts-rounded ts-w-56 ts-text-sm focus:ts-outline-none" placeholder="-1"
							:class="{
								'ts-border-red-500': errors.has(`component_items.${index}.actual_score`),
								'ts-text-red-500': score.actual_score > score.max_score
							}">
						<div class="ts-text-red-500" v-if="errors.has(`component_items.${index}.actual_score`)">
							{{ errors.first(`component_items.${index}.actual_score`) }}
						</div>
					</div>
					<div class="ts-text-xs ts-text-gray-500">Max: {{ score.max_score }}</div>
				</div>
			</div>
			<!-- button -->
			<div class="ts-flex ts-space-x-2 ts-justify-end ts-mt-3 ts-bg-white ts-sticky" style="bottom: 10px;">
				<ts-button @click="onClose">{{ $t('cancel') }}</ts-button>
				<ts-button @click="onStore" color="primary">{{ $t('save') }}</ts-button>
			</div>
		</ts-loading-banner>
	</div>
</template>

<script>
import { formViewModel, storeSubComponent } from '@/api/student-scoring-table-entry';
import { Errors } from 'form-backend-validation';
export default {
	name: 'scoreComponentEntry',
	props: [
		'value',
		'student',
		'component',
		'subjectHeader'
	],
	data() {
		return {
			loading: false,
			model: {},
			errors: new Errors(),
			isWarningModalOpen: false,
			componentModels: {
				student_id: undefined,
				st_class_id: undefined,
				acp_id: undefined,
				class_id: undefined,
				grade_id: undefined,
				ac_year_id: undefined,
				ac_term_id: undefined,
				scoring_cycle_id: undefined,
				cycle_value: undefined,
				subject_comp_id: undefined,
				component_items: []
			}
		}
	},
	computed: {
		getSessionName() {
			if (this.subjectHeader) {
				return this.subjectHeader[0].name
			}
			return null;
		},
		componentName() {
			if (this.subjectHeader) {
				const itemPointer = this.subjectHeader[0]
				if (itemPointer) {
					return itemPointer.items.find((i) => i.sc_item_subject_comp_id == this.component.sc_item_subject_comp_id)
				}
			}
			return null;
		}
	},
	created() {
		this.model = this.value
		this.clearInitial()
		this.initialValue()
		this.fetchSubItem()
	},
	methods: {
		handleWheel(event) {
			event.preventDefault();
		},
		validateInput(score, max) {
			if (score.actual_score > max) {
				this.isWarningModalOpen = true;
				this.$warning({
					centered: true,
					title: 'This is a warning message',
					content: `The entered score (${score.actual_score}) exceeds the maximum allowed (${max}).`,
					keyboard: true, // Enable keyboard support
					onOk: () => {
						score.actual_score = null
						const input = this.$refs[`student_${this.student.student_id}_${score.subject_comp_detail_id}`]
						if (input.length > 0) {
							input[0].focus()
						} else if (input.length == 0) {
							input.focus()
						}
					}
				});
			} else {
				this.isWarningModalOpen = false
			}
		},
		onStore() {
			if (this.isWarningModalOpen) return;
			this.loading = true
			this.errors = new Errors()
			storeSubComponent(this.componentModels)
				.then(response => {
					this.$toasted.success(response.message)
						this.$emit('set-score', response[0])
				})
				.catch(error => {
					this.$toasted.error(error.message)
					this.errors = new Errors(error.errors)
				})
				.finally(() => (this.loading = false))
		},
		fetchSubItem() {
			this.loading = true
			return formViewModel({
				params: {
					fnName: 'getSubComponentSubject',
					sc_subject_id: this.component.sc_subject_id,
					sc_item_subject_comp_id: this.component.sc_item_subject_comp_id,
					st_class_id: this.student.st_class_id,
					student_id: this.student.student_id,
					scoring_cycle_id: this.model.scoring_cycle_id,
					cycle_value: this.model.cycle_value,
				}
			})
				.then(({ data }) => {
					this.componentModels.component_items = data.getSubComponentSubject
				})
				.catch(error => {
					this.$toasted.error(error.message)
				})
				.finally(() => {
					this.loading = false
				})
		},
		clearInitial() {
			this.componentModels.student_id = undefined
			this.componentModels.st_class_id = undefined
			this.componentModels.acp_id = undefined
			this.componentModels.class_id = undefined
			this.componentModels.grade_id = undefined
			this.componentModels.ac_year_id = undefined
			this.componentModels.ac_term_id = undefined
			this.componentModels.scoring_cycle_id = undefined
			this.componentModels.cycle_value = undefined
			this.componentModels.subject_comp_id = undefined
			this.componentModels.component_items = []
		},
		initialValue() {
			this.componentModels.student_id = this.student.student_id
			this.componentModels.st_class_id = this.student.st_class_id
			this.componentModels.acp_id = this.model.acp_id
			this.componentModels.class_id = this.model.class_id
			this.componentModels.grade_id = this.model.grade_id
			this.componentModels.ac_year_id = this.model.ac_year_id
			this.componentModels.ac_term_id = this.model.ac_term_id
			this.componentModels.scoring_cycle_id = this.model.scoring_cycle_id
			this.componentModels.cycle_value = this.model.cycle_value
			this.componentModels.subject_comp_id = this.component.sc_item_subject_comp_id
			this.componentModels.component_items = []
		},
		onClose() {
			this.clearInitial()
			this.$emit('close')
		}
	}
}
</script>

<style scoped>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}
</style>